/**
 * Pure helpers for Reporting Live chat UI (testable, no React).
 */

import { evaluateChatSlaBreach } from "@/lib/slaBreach";

/** @param {number} handoffMs @returns {string} */
function isoFromMs(handoffMs) {
  const t = Number(handoffMs);
  if (!Number.isFinite(t) || t <= 0) return new Date(0).toISOString();
  return new Date(t).toISOString();
}

/**
 * Map BE open session row to slaMeta shape expected by {@link evaluateChatSlaBreach}.
 * Human-agent reply times come from messages, never session.first_response_time
 * (that column is set on first reply and would hide a first-response breach).
 * @param {{
 *   handoff_time: number,
 *   first_user_message_at?: number | null,
 *   last_user_message_at?: number | null,
 *   first_human_agent_message_at?: number | null,
 *   last_human_agent_message_at?: number | null,
 *   first_response_time?: number | null,
 * }} row
 */
export function openSessionToSlaMeta(row) {
  const ho = Number(row?.handoff_time);
  const firstUserRaw =
    row?.first_user_message_at != null ? Number(row.first_user_message_at) : ho;
  const lastUserRaw =
    row?.last_user_message_at != null ? Number(row.last_user_message_at) : firstUserRaw;
  const firstAgentRaw =
    row?.first_human_agent_message_at != null ? Number(row.first_human_agent_message_at) : null;
  const lastAgentRaw =
    row?.last_human_agent_message_at != null
      ? Number(row.last_human_agent_message_at)
      : firstAgentRaw;
  return {
    firstUserAt: isoFromMs(firstUserRaw),
    lastUserAt: isoFromMs(lastUserRaw),
    firstHumanAgentAt:
      firstAgentRaw != null && Number.isFinite(firstAgentRaw) ? new Date(firstAgentRaw).toISOString() : null,
    lastHumanAgentAt:
      lastAgentRaw != null && Number.isFinite(lastAgentRaw) ? new Date(lastAgentRaw).toISOString() : null,
  };
}

/**
 * @param {Array<{ handoff_time: number, first_response_time: number | null, agent_id: string | null }>} openSessions
 * @param {{ chatFirstResponseSeconds: number, chatAverageResponseSeconds: number }} settings
 * @param {number} [nowMs]
 */
export function countChatSlaAtRisk(openSessions, settings, nowMs = Date.now()) {
  if (!Array.isArray(openSessions) || !settings) return 0;
  let n = 0;
  for (const row of openSessions) {
    const meta = openSessionToSlaMeta(row);
    if (evaluateChatSlaBreach(meta, settings, nowMs, row?.status).breached) n += 1;
  }
  return n;
}

/**
 * @param {string | null} agentIdStr
 * @param {Array<{ handoff_time: number, first_response_time: number | null, agent_id: string | null }>} openSessions
 * @param {Record<string, unknown>} settings
 * @param {number} [nowMs]
 */
export function countChatSlaAtRiskForAgent(agentIdStr, openSessions, settings, nowMs = Date.now()) {
  if (!agentIdStr || !Array.isArray(openSessions)) return 0;
  const needle = String(agentIdStr).trim();
  let n = 0;
  for (const row of openSessions) {
    if (String(row?.agent_id ?? "").trim() !== needle) continue;
    if (evaluateChatSlaBreach(openSessionToSlaMeta(row), settings, nowMs, row?.status).breached) n += 1;
  }
  return n;
}

/**
 * @param {{ open_count: number, resolved_count: number, conversation_total: number }} overview
 */
export function buildChatStatusShareRows(overview) {
  const total = Number(overview?.conversation_total) || 0;
  if (total <= 0) return [];
  const open = Number(overview?.open_count) || 0;
  const resolved = Number(overview?.resolved_count) || 0;
  return [
    { label: "Open", count: open, value: Math.round((open / total) * 100), color: "#0d9488" },
    { label: "Resolved", count: resolved, value: Math.round((resolved / total) * 100), color: "#10b981" },
  ];
}

/**
 * @param {Array<{ day?: string, date?: string, open?: number, resolved?: number }>} data
 */
export function chatVolumeMaxFromSeries(data) {
  if (!Array.isArray(data) || !data.length) return 1;
  let m = 1;
  for (const d of data) {
    const o = Number(d?.open) || 0;
    const r = Number(d?.resolved) || 0;
    if (o > m) m = o;
    if (r > m) m = r;
  }
  return m;
}

/**
 * @param {Array<{ day?: string, date?: string, open?: number, resolved?: number }>} data
 */
export function chatVolumeDaysForVolumeBars(data) {
  if (!Array.isArray(data)) return [];
  return data.map((d) => ({
    day: d.day ?? "",
    date: d.date ?? d.day ?? "",
    open: Number(d.open) || 0,
    resolved: Number(d.resolved) || 0,
  }));
}

/**
 * @param {Record<string, unknown> | null} overview
 * @param {number} transferTotal
 * @param {number} slaRiskCount
 */
export function buildChatKpis(overview, transferTotal, slaRiskCount) {
  const open = Number(overview?.open_count) || 0;
  const total = Number(overview?.conversation_total) || 0;
  const resolved = Number(overview?.resolved_count) || 0;
  const un = Number(overview?.unassigned_open) || 0;
  return [
    {
      id: "ch-open",
      label: "Open live chats",
      value: String(open),
      trend: un ? `${un} unassigned` : "Inbox balanced",
      trendUp: un === 0,
      icon: "ri-chat-3-line",
      color: "#0d9488",
    },
    {
      id: "ch-total",
      label: "Conversations in workspace",
      value: String(total),
      trend: `${resolved} resolved`,
      trendUp: true,
      icon: "ri-discuss-line",
      color: "#14b8a6",
    },
    {
      id: "ch-sla",
      label: "Chats — SLA at risk",
      value: String(slaRiskCount),
      trend: "Open threads breaching chat SLA",
      trendUp: slaRiskCount === 0,
      icon: "ri-timer-flash-line",
      color: "#e11d48",
    },
    {
      id: "ch-transfer",
      label: "Chat transfer handoffs",
      value: String(transferTotal),
      trend: "System transfer events (window)",
      trendUp: true,
      icon: "ri-shuffle-line",
      color: "#7c3aed",
    },
  ];
}
